import React, { useEffect, useRef, useCallback } from 'react';
import { useReactToPrint } from 'react-to-print';
import { useLocation } from 'react-router-dom';
import enslogo from '../../assets/images/logos/ens-logo-dark.png';
import Paper from '@mui/material/Paper';
import { useNavigate } from 'react-router-dom';
import { Container, Box } from '@mui/material';
import { dateFormatChange } from '../../utils/date';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';

const STATUS_COLUMN_WIDTH = 200;

const RenderTable = ({ columnId, columns, rows }) => {
  if (!columnId) {
    // column ID is required as it is used for keys in maps
    return <div>Error rendering table - column ID is required</div>;
  }

  // ENS-487: We are using separate TableBody instead of TableHead for first row as otherwise
  // the table header duplicates on every page, which is unwanted behaviour
  return (
    <TableContainer component={Paper} sx={{ width: '100%' }}>
      <Table>
        <TableBody className="theme--header">
          <TableRow>
            {columns.map((column) => {
              return (
                <TableCell
                  align={column.headerAlign}
                  sx={{ maxWidth: column.maxWidth || 'auto' }}
                  key={`${columnId}-col-${column.field}}`}
                >
                  {column.headerName}
                </TableCell>
              );
            })}
          </TableRow>
        </TableBody>
        <TableBody>
          {rows.map((row) => {
            return (
              <TableRow key={`${columnId}-row-${row.id}}`}>
                {columns.map((column) => {
                  return (
                    <TableCell
                      align={column.align}
                      sx={{ maxWidth: column.maxWidth || 'auto' }}
                      key={`${columnId}-cell-${column.field}}`}
                    >
                      {column.renderCell
                        ? column.renderCell({
                            row,
                            value: row[column.field],
                          })
                        : row[column.field]}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default function PrintablePage() {
  // const {shipmentDataView,consignmentDataView} = props;
  const { state } = useLocation();
  let navigate = useNavigate();
  const componentRef = useRef(null);

  const handleAfterPrint = useCallback(() => {
    console.log('`onAfterPrint` called');
  }, []);

  const handleBeforePrint = useCallback(() => {
    console.log('`onBeforePrint` called');
    return Promise.resolve();
  }, []);

  const printFn = useReactToPrint({
    contentRef: componentRef,
    documentTitle: 'Shipment Page',
    onAfterPrint: handleAfterPrint,
    onBeforePrint: handleBeforePrint,
  });

  const columns = [
    {
      field: 'clientJobNumber',
      headerName: 'Your Reference',
      editable: true,
      headerClassName: 'theme--header',
      flex: 1,
    },
    {
      field: 'vehicleIdentity',
      headerName: 'Vehicle Identity',
      editable: true,
      headerClassName: 'theme--header',
      flex: 1,
    },
    {
      field: 'departureDate',
      headerName: 'Departure Date',
      type: 'number',
      editable: true,
      headerClassName: 'theme--header',
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => <div>{params.row.departureDate ? dateFormatChange(params.row.departureDate) : ''}</div>,
    },
    {
      field: 'arrivalDateTime',
      headerName: 'Arrival Date',
      editable: true,
      headerClassName: 'theme--header',
      flex: 1,
      align: 'left',
      headerAlign: 'left',
      renderCell: (params) => (
        <div>{params.row.arrivalDateTime ? dateFormatChange(params.row.arrivalDateTime) : ''}</div>
      ),
    },
    {
      field: 'totalConsignments',
      headerName: '# of Consignments',
      editable: true,
      headerClassName: 'theme--header',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'status',
      headerName: 'Status',
      maxWidth: STATUS_COLUMN_WIDTH,
      editable: true,
      headerClassName: 'theme--header',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        return (
          <Box
            sx={{
              backgroundColor: '#fff',
              width: '100%',
              height: '100%',
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
  ];
  const consignmentColumns = [
    {
      field: 'consignor',
      headerName: 'Consignor',
      editable: true,
      headerClassName: 'theme--header',
      flex: 1,
    },
    {
      field: 'consignee',
      headerName: 'Consignee',
      editable: true,
      headerClassName: 'theme--header',
      flex: 1,
    },
    {
      field: 'totalItemsCounts',
      headerName: '# of Lines',
      type: 'number',
      maxWidth: 100,
      editable: true,
      headerClassName: 'theme--header',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'mrn',
      headerName: 'MRN',
      editable: true,
      headerClassName: 'theme--header',
      maxWidth: 220,
      flex: 1,
    },
    {
      field: 'status',
      headerName: 'Status',
      editable: true,
      headerClassName: 'theme--header',
      maxWidth: STATUS_COLUMN_WIDTH,
      align: 'center',
      headerAlign: 'center',
      flex: 1,
    },
  ];
  useEffect(() => {
    document.body.classList.add('print-page');
    return () => {
      document.body.classList.remove('print-page');
    };
    // printFn();
  }, []);
  return (
    <>
      <Container ref={componentRef}>
        <div className="printable-header">
          <Box
            sx={{
              mr: 2,
              display: 'flex',
              textDecoration: 'none',
              width: '50%',
            }}
          >
            <img
              src={enslogo}
              alt="ENS Logo"
              style={{
                height: '90px',
                width: 'auto',
                marginTop: '10px',
                marginBottom: '10px',
              }}
            />
          </Box>
          <Box
            sx={{
              mr: 2,
              display: 'flex',
              textDecoration: 'none',
              width: '50%',
              justifyContent: 'flex-end',
              position: 'relative',
            }}
          >
            <p>S&amp;S GB</p>
            <span>{sessionStorage.getItem('company')}</span>
          </Box>
        </div>
        <div className="rowWrapper shipment-view-wrapper printable-page">
          <div className="full-width">
            <div className="header-text">Shipment Details</div>
            <div>
              <RenderTable columnId="shipment-data" columns={columns} rows={state.shipmentDataView} />
            </div>
          </div>
          <div className="full-width">
            <div className="header-text">Consignment Details</div>
            <div className="header-text-right">Lines: {state.consignmentDataView?.length}</div>
            <div>
              <RenderTable
                columnId="consignment-details"
                columns={consignmentColumns}
                rows={state.consignmentDataView}
              />
            </div>
          </div>
        </div>
      </Container>
      <Container>
        <div className="printable-header">
          <Box
            className="only-print"
            sx={{
              mr: 2,
              display: { md: 'flex' },
              textDecoration: 'none',
              width: '50%',
              justifyContent: 'flex-start',
            }}
          >
            <button onClick={() => navigate(-1)}>Back</button>
          </Box>
          <Box
            className="only-print"
            sx={{
              display: { md: 'flex' },
              textDecoration: 'none',
              width: '50%',
              justifyContent: 'flex-end',
            }}
          >
            <button onClick={printFn}>Print</button>
          </Box>
        </div>
      </Container>
    </>
  );
}
